import { Action, ActionReducer, createReducer, on } from '@ngrx/store';
import { Card, ListQuery, CardView } from './models/card.model';
import * as listsActions from './lists.actions';
import { ExportTemplate } from 'src/app/modules/management/store/datasets';
import produce from 'immer';
import { RelatedDocumentsRequest } from '@app/modules/core/store/models/document.model';
import { ListsTableType } from './models/table-type.enum';
export const listsFeatureKey = 'lists';

export interface ListsState {
  linkDocumentUId?: string | null;
  linkDocumentDatasetUId?: string | null;
  loadedCards: Card[];
  cardViewForDisplay: CardView;
  ui?: {
    cardsVisible?: boolean;
    isTableVisible?: boolean;
    tableType?: ListsTableType;
  };
  listQuery: ListQuery;
  assetsListQuery: ListQuery;
  selectListQuery: ListQuery;
  relatedListQuery: ListQuery;
  exportTemplates?: ExportTemplate[];
}

export const initialListsState: ListsState = {
  linkDocumentUId: null,
  linkDocumentDatasetUId: null,
  loadedCards: [],
  cardViewForDisplay: null,
  ui: {
    cardsVisible: false,
    isTableVisible: false,
    tableType: null
  },
  listQuery: null,
  assetsListQuery: null,
  selectListQuery: null,
  relatedListQuery: null,
  exportTemplates: null
};

export const reducer: ActionReducer<ListsState, Action> = createReducer(
  initialListsState,

  on(listsActions.hideTableToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isTableVisible: false,
        tableType: ListsTableType.Documents
      }
    };
  }),
  on(listsActions.showTableToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isTableVisible: true,
        tableType: action.tableType
      }
    };
  }),
  on(listsActions.setTableTypeToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        tableType: action.tableType
      }
    };
  }),
  on(listsActions.showAssetsTableToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isTableVisible: true,
        tableType: ListsTableType.Assets
      }
    };
  }),
  on(listsActions.showRelatedTableToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        tableType: ListsTableType.Related,
        isTableVisible: true
      }
    };
  }),
  on(listsActions.hideTableShowCardToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        isTableVisible: false,
        cardsVisible: !action.showSelectedDocument
      }
    };
  }),
  on(listsActions.showCardsToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        cardsVisible: true
      }
    };
  }),
  on(listsActions.hideCardsToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        cardsVisible: false
      }
    };
  }),
  on(listsActions.setCardDataToStore, (state: ListsState, action) => {
    return {
      ...state,
      loadedCards: action.cardView.cards,
      cardViewForDisplay: action.cardView
    };
  }),
  on(listsActions.setCardVisibilityToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        cardsVisible: action.isVisible
      }
    };
  }),
  on(listsActions.hideDisplayCardToStore, (state: ListsState, action) => {
    return {
      ...state,
      loadedCards: [],
      cardViewForDisplay: {
        ...state.cardViewForDisplay,
        cards: [],
        htmlColorCode: null,
        summaryName: null
      },
      ui: {
        ...state.ui,
        cardsVisible: false
      }
    };
  }),
  on(listsActions.clearCardsToStore, (state: ListsState, action) => {
    return {
      ...state,
      listQuery: null,
      loadedCards: [],
      cardViewForDisplay: {
        ...state.cardViewForDisplay,
        cards: [],
        htmlColorCode: null,
        summaryName: null
      },
      ui: {
        ...state.ui,
        cardsVisible: false
      }
    };
  }),
  on(listsActions.setCardsForDisplayToStore, (state: ListsState, action) => {
    return {
      ...state,
      cardViewForDisplay: {
        ...state.cardViewForDisplay,
        cards: action.cards
      }
    };
  }),
  on(listsActions.setCardSearchTermToStore, (state: ListsState, action) => {
    return {
      ...state,
      ui: {
        ...state.ui,
        cardSearchTerm: action.searchTerm
      }
    };
  }),
  on(listsActions.loadExportTemplatesCompleteToStore, (state, action) => {
    const results = action.results?.data?.filter(t => !!t.fileName && t.isValid === true);
    return {
      ...state,
      exportTemplates: results
    };
  }),
  on(listsActions.setListQueryToStore, (state, action) => {
    return {
      ...state,
      listQuery: action.query
    };
  }),
  on(listsActions.setAssetsListQueryToStore, (state, action) => {
    return {
      ...state,
      assetsListQuery: action.query
    };
  }),
  on(listsActions.setSelectListQueryToStore, (state, action) => {
    return {
      ...state,
      selectListQuery: action.query
    };
  }),
  on(listsActions.setRelatedListQueryToStore, (state, action) => {
    return {
      ...state,
      relatedListQuery: action.query
    };
  }),
  on(listsActions.setLayerQueryDataUpdateTime, (state, action) => {
    let updatedListQuery = state.listQuery;
    if (!!state.listQuery) {
      updatedListQuery = produce(state.listQuery, draft => {
        draft.dataUpdateTime[action.layerUId] = new Date().getTime();
      });
    }

    return {
      ...state,
      listQuery: updatedListQuery
    };
  }),

  on(listsActions.clearListsToStore, (state, action) => {
    return {
      ...initialListsState
    };
  }),
  on(listsActions.loadExportTemplatesCompleteToStore, (state, action) => {
    const results = action.results?.data?.filter(t => !!t.fileName && t.isValid === true);
    return {
      ...state,
      exportTemplates: results
    };
  })
);

export const getLoadedCards = (state: ListsState) => state?.loadedCards;
export const getCardViewForDisplay = (state: ListsState) => state?.cardViewForDisplay;
export const getListQuery = (state: ListsState) => state?.listQuery;
export const getAssetsListQuery = (state: ListsState) => state?.assetsListQuery;
export const getSelectListQuery = (state: ListsState) => state?.selectListQuery;
export const getIsCardsVisible = (state: ListsState) => state?.ui?.cardsVisible;
export const getIsTableVisible = (state: ListsState) => state?.ui?.isTableVisible;
export const getTableType = (state: ListsState) => state?.ui?.tableType;
export const getRelatedListQuery = (state: ListsState) => state?.relatedListQuery;

// Export Templates
export const getExportTemplates = (state: ListsState) => state?.exportTemplates;
